import type {
  AttendanceShift,
  AttendanceShiftsByDay,
  AttendanceWeekday,
  DayCenterSchedule,
} from '@seen/core/models/schedules/dayCenterSchedule'
import { Button, cn, Separator } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import { compact } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import {
  formatAttendanceDayRanges,
  groupDaysByShift,
  SHIFT_LABELS,
} from '#src/components/patient/detail-content/day-center-schedule/weekdays.ts'
import { DIVIDER_DOT_COLOR_CLASS } from '#src/components/patient/dividerDot.ts'

// The small AM/PM chip beside a group of days. Raw hex values match the
// facesheet redesign Figma, whose stone palette the app theme has not adopted.
// Dark mirrors the stone steps (100 → 800 fill, 300 text, 10:1 on its fill),
// with a stone-500 border so the pill's edge clears 3:1 on the dark card.
export function ShiftPill({ shift }: { shift: AttendanceShift }): JSX.Element {
  return (
    <span className="rounded border border-[#e3e1dc] bg-[#f5f5f4] px-1 leading-4 text-[#79716b] dark:border-[#79716b] dark:bg-[#292524] dark:text-[#d6d3d1]">
      {SHIFT_LABELS[shift]}
    </span>
  )
}

// The day-center schedule as it reads on the facesheet header row: attended
// days grouped by shift on one wrapping line, "Mon, Wed [AM] · Thu [PM]".
//
// With `hangingSeparators`, the middot between groups becomes an
// absolutely-positioned sibling that hangs into the column gap instead of a
// laid-out flex item. When a group wraps to its own line it sits at the row's
// left edge and its bullet, pushed left of it, is clipped by overflow-hidden —
// so wrapped lines don't start or carry a stray middot (Gio's narrow-card
// Figma). Off, the middots render inline as they ship today.
//
// This row's inline middot is the header's reference divider — the glyph, color
// and 6px-a-side spacing the rest of the header standardizes on. `standardDots`
// brings the hanging form onto that same spacing, which the margin it ships
// with only achieves on the dot's right.
export function AttendanceDaysInline({
  days,
  shifts,
  hangingSeparators = false,
  standardDots = false,
}: {
  days: AttendanceWeekday[]
  shifts: AttendanceShiftsByDay
  hangingSeparators?: boolean
  standardDots?: boolean
}): JSX.Element {
  const groups = groupDaysByShift(days, shifts)
  if (hangingSeparators) {
    return (
      <span
        className={cn(
          'flex flex-wrap gap-y-0.5 overflow-hidden',
          // Widened to the dot's whole footprint (6px + glyph + 6px) so the
          // hanging dot can sit centered in the gap.
          standardDots ? 'gap-x-4' : 'gap-x-3',
          'items-center'
        )}
      >
        {groups.map((group, index) => (
          <span
            key={group.shift ?? 'unshifted'}
            className="relative flex items-center gap-1 whitespace-nowrap"
          >
            {index > 0 && (
              <span
                aria-hidden
                className={cn(
                  'absolute right-full',
                  // A box spanning the whole column gap with the glyph centered
                  // in it clears both sides by the same amount; a margin on an
                  // auto-width box can only set the right.
                  standardDots ? 'w-4 text-center' : 'mr-1.5',
                  standardDots ? DIVIDER_DOT_COLOR_CLASS : 'text-[#d6d3d1]'
                )}
              >
                •
              </span>
            )}
            {formatAttendanceDayRanges(group.days)}
            {group.shift && <ShiftPill shift={group.shift} />}
          </span>
        ))}
      </span>
    )
  }
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      {groups.flatMap((group, index) => {
        const key = group.shift ?? 'unshifted'
        const groupEl = (
          <span key={key} className="flex items-center gap-1 whitespace-nowrap">
            {formatAttendanceDayRanges(group.days)}
            {group.shift && <ShiftPill shift={group.shift} />}
          </span>
        )
        return index === 0
          ? [groupEl]
          : [
              <span
                key={`separator-${key}`}
                aria-hidden
                // This dot is the spec the rest of the header matches, so it
                // takes the shared color too — its light value is unchanged,
                // and only the flag adds the dark counterpart it has always
                // been missing.
                className={
                  standardDots ? DIVIDER_DOT_COLOR_CLASS : 'text-[#d6d3d1]'
                }
              >
                •
              </span>,
              groupEl,
            ]
      })}
    </span>
  )
}

// The detail popover shown when hovering the facesheet header's schedule row:
// center days and preferred external-appointment days grouped by shift, plus
// any free-text note. Sections with no data are omitted.
export function DayCenterScheduleHoverCard({
  schedule,
  onEdit,
}: {
  schedule: DayCenterSchedule
  onEdit?: () => void
}): JSX.Element {
  const attendanceDays = schedule.attendanceDays
  const preferredDays = schedule.preferredAppointmentDays
  const note = schedule.note
  // Only sections with data render, so an empty schedule shows no orphaned
  // labels; a hairline divider then falls between each surviving pair.
  const sections = compact([
    attendanceDays.length > 0 && (
      <ScheduleDetailRow key="center" label="Center days">
        <ShiftDayGroups
          days={attendanceDays}
          shifts={schedule.attendanceShifts}
        />
      </ScheduleDetailRow>
    ),
    preferredDays.length > 0 && (
      <ScheduleDetailRow
        key="preferred"
        label="External appointment preferences"
      >
        <ShiftDayGroups
          days={preferredDays}
          shifts={schedule.preferredAppointmentShifts}
        />
      </ScheduleDetailRow>
    ),
    note && (
      <div key="note" className="flex flex-col gap-1 text-xs leading-4">
        <span className="text-[#292524] dark:text-foreground">Notes</span>
        <span className="text-[#57534d] dark:text-foreground-secondary">
          {note}
        </span>
      </div>
    ),
  ])
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-6 items-center gap-1">
        <p className="flex-1 text-base leading-6 font-semibold tracking-[-0.53px] text-[#1c1917] dark:text-foreground">
          Day Center Schedule
        </p>
        {onEdit && (
          <Button
            variant="ghost"
            size="unstyled"
            aria-label="Edit day center schedule"
            className="-m-0.5 rounded p-0.5"
            onClick={onEdit}
          >
            <IconPencil className="size-3" />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {sections.flatMap((section, index) =>
          index === 0
            ? [section]
            : [<Separator key={`separator-${section.key}`} />, section]
        )}
      </div>
    </div>
  )
}

// A label/value row in the hover card: label on the left, day groups stacked
// and right-aligned on the right.
function ScheduleDetailRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-2 text-xs leading-4">
      <span className="flex-1 text-[#292524] dark:text-foreground">
        {label}
      </span>
      <div className="flex flex-1 flex-col items-end gap-0.5 text-[#57534d] dark:text-foreground-secondary">
        {children}
      </div>
    </div>
  )
}

// The stacked variant used inside the hover card: one right-aligned line per
// shift group.
function ShiftDayGroups({
  days,
  shifts,
}: {
  days: AttendanceWeekday[]
  shifts: AttendanceShiftsByDay
}): JSX.Element {
  const groups = groupDaysByShift(days, shifts)
  return (
    <>
      {groups.map((group) => (
        <span
          key={group.shift ?? 'unshifted'}
          className="flex items-center justify-end gap-1 text-right"
        >
          {formatAttendanceDayRanges(group.days)}
          {group.shift && <ShiftPill shift={group.shift} />}
        </span>
      ))}
    </>
  )
}
